<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="$v.description.$model"
          :error-messages="getValidationErrorText($v.description)"
          label="Наименование и реквизиты документа"
          outlined
        />
      </v-col>
      <template v-if="!addDocumentForm">
        <v-col cols="12">
          <div class="result">
            <div>
              <a :href="getFileLink(file.link)">{{ file.name }}</a>
              <v-btn
                class="ml-3"
                depressed
                color="transparent"
                x-small
                fab
                :loading="loading"
                @click="removeOnResultFile"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <div>
              <a :href="getFileLink(fileSig.link)">{{ fileSig.name }}</a>
              <v-btn
                class="ml-3"
                depressed
                color="transparent"
                x-small
                fab
                :loading="loading"
                @click="removeOnResultFile"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-col>
      </template>
      <template class="add-form-file" v-else>
        <v-col cols="12">
          <v-file-input
            v-model="$v.file.$model"
            accept=".pdf"
            @click:clear="reset(InputType.FILE)"
            :error-messages="getValidationErrorText($v.file)"
            label="Прикладываемый документ"
            outlined
          />
        </v-col>
        <v-col>
          <v-file-input
            v-model="$v.fileSig.$model"
            accept=".sig"
            @click:clear="reset(InputType.FILE_SIG)"
            :disabled="!Boolean(file)"
            :error-messages="getValidationErrorText($v.fileSig)"
            label="Прикладываемая ЭП документа"
            outlined
          />
        </v-col>
        <v-col cols="auto">
          <v-btn
            @click="signFile"
            :disabled="Boolean(fileSig) || !Boolean(file)"
            depressed
            class="button-base-white"
            :loading="loadingSign"
          >
            Подписать
          </v-btn>
        </v-col>
      </template>
    </v-row>

    <div v-if="error" class="error--text">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { maxLength, required } from 'vuelidate/lib/validators';
import {
  EMOJI,
  FILE_NAME_MAX_LENGTH,
  FILE_SIG_NAME,
  FILE_SIZE,
  FILE_TYPE,
  getValidationErrorText,
} from '@/utils/validate';
import { DocumentType } from '@/types/services';
import { ErrorAlertModule } from '@/store/modules/errors';
import {
  deleteFile,
  getFileLink,
  saveFile,
  saveSigFile,
} from '@/api/filestorage';
import cloneDeep from 'clone-deep';
import { ServicesModule } from '@/store/modules/services';
import { errorHandler, isNotEmptyObjectCheck } from '@/utils/common';
import { checkSign } from '@/api/checkSign';

const InputType = {
  FILE: 'file',
  FILE_SIG: 'fileSig',
};

export default {
  name: 'AddingRegularFileForm',
  validations() {
    return {
      description: {
        required,
        maxLength: maxLength(this.maxLength),
        EMOJI,
      },
      file: {
        required,
        FILE_TYPE: FILE_TYPE(['.pdf']),
        FILE_SIZE: FILE_SIZE(25),
        FILE_NAME_MAX_LENGTH: FILE_NAME_MAX_LENGTH(100),
      },
      fileSig: {
        required,
        FILE_TYPE: FILE_TYPE(['sig']),
        FILE_SIZE: FILE_SIZE(25),
        FILE_NAME_MAX_LENGTH: FILE_NAME_MAX_LENGTH(100),
        FILE_SIG_NAME: FILE_SIG_NAME(this.file),
      },
    };
  },
  props: {
    maxLength: {
      type: Number,
      default: 1e6,
      required: false,
    },
    documents: {
      type: Array,
    },
    document: {
      type: Object,
    },
  },
  data: () => ({
    InputType,
    getValidationErrorText,
    getFileLink,
    type: DocumentType.FILE,
    id: null,
    description: '',
    file: null,
    fileSig: null,
    error: '',

    isSigningPlugin: false,
    loading: false,
    loadingSign: false,
    loadingRemove: false,
    selectedType: '',
    addDocumentForm: true,
  }),

  created() {
    if (isNotEmptyObjectCheck(this.document)) {
      this.id = this.document.id;
      this.description = this.document.description;
      this.file = this.document.file;
      this.fileSig = this.document.fileSig;
    }

    if (this.file && this.fileSig) {
      this.addDocumentForm = false;
      this.fileSig.size = 287144;
    }
  },
  methods: {
    reset(inputType) {
      if (inputType === InputType.FILE) this.file = null;

      if (!inputType) {
        this.description = '';
        this.file = null;
      }

      this.$v.$reset();
      this.fileSig = null;
      this.isSigningPlugin = false;
    },

    touch() {
      this.$v.$touch();
      return this.$v.$invalid;
    },

    async upload() {
      if (!this.touch()) {
        this.loading = true;
        ErrorAlertModule.setErrorAlert('');
        this.error = '';

        if (!this.file.md5) {
          try {
            if (this.isSigningPlugin) {
              const file = await saveFile(this.file);
              const fileSig = await saveSigFile({
                sigBas64: this.fileSig.sigBas64,
                fileName: this.fileSig.fileName,
              });
              const document = {
                id: this.id ?? null,
                type: DocumentType.FILE,
                description: this.description,
                file,
                fileSig,
              };

              this.dialogCloseHandler();

              return document;
            }

            const { file, signFile, message, status } = await checkSign(
              this.file,
              this.fileSig
            );

            if (status === 'OK') {
              const document = {
                id: this.id ?? null,
                type: DocumentType.FILE,
                description: this.description,
                file,
                fileSig: signFile,
              };

              this.dialogCloseHandler();

              return document;
            }

            this.error = message;

            this.loading = false;
          } catch (error) {
            errorHandler(error);

            this.dialogCloseHandler();

            await this.$vuetify.goTo('#header-target', { duration: 200 });
          }
        } else {
          const document = {
            id: this.id ?? null,
            type: DocumentType.FILE,
            description: this.description,
            file: this.file,
            fileSig: this.fileSig,
          };

          this.dialogCloseHandler();

          return document;
        }
      }
    },

    dialogCloseHandler() {
      this.reset();
      this.loading = false;
      this.dialog = false;
    },

    async remove(fileSigLink) {
      this.loadingRemove = true;
      ErrorAlertModule.setErrorAlert('');

      try {
        await deleteFile(fileSigLink);

        const deletedIndex = this.documents.findIndex(
          (document) => document.fileSig.link === fileSigLink
        );

        const documents = cloneDeep(this.documents);
        documents.splice(deletedIndex, 1);

        this.$emit('change', documents);
      } catch (error) {
        errorHandler(error);
      }

      this.loadingRemove = false;
    },

    async removeOnResultFile() {
      this.loading = true;
      ErrorAlertModule.setErrorAlert('');

      try {
        await deleteFile(this.file.link);
        this.addDocumentForm = true;
        this.file = null;
        this.fileSig = null;
      } catch (error) {
        errorHandler(error);
      }

      this.loading = false;
    },

    signFile() {
      // Создать саму подпись
      const file = this.file;
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        this.loadingSign = true;

        let header = ';base64,';
        let fileData = reader.result;
        let fileContent = fileData.substr(
          fileData.indexOf(header) + header.length
        );
        try {
          const signs = await ServicesModule.runSign({
            sigBas64: fileContent,
          });
          this.isSigningPlugin = true;
          this.fileSig = {
            sigBas64: signs.sigBas64[0],
            fileName: file.name,
            name: `${file.name}.sig`,
          };
        } catch {
          await this.$vuetify.goTo('#header-target', { duration: 200 });
        }

        this.loadingSign = false;
      };
    },
  },
};
</script>

<style scoped></style>
